//@flow
import * as Immutable from 'immutable';

import {
    ExtendedUser,
    UserStatusWrapper,
    LocationInstant,
    UserTaskInfo,
    UserEventInfo,
    CriticalEvent,
    UserEffectiveStatus
} from 'common/models/flow/ExtendedUser';
import type {
    ExtendedUserModelType,
    UserStatusWrapperModelType,
    LocationInstantModelType,
    UserTaskInfoModelType,
    UserEventInfoModelType,
    CriticalEventModelType,
    UserEffectiveStatusModelType
} from 'common/models/flow/ExtendedUser';

import { Skills } from './Skills';
import { Team } from './Team';
import { ZoneMapGeometry } from './ZoneMapGeometry';
import I18nHelper from 'common/helpers/I18nHelper';

const translationsNamespace = ['liveView'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

class InternalUserEffectiveStatus extends UserEffectiveStatus {
    static fromJS(
        json: UserEffectiveStatusModelType
    ): InternalUserEffectiveStatus {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

export const UserStatus = {
    IDLE: 'IDLE',
    ACTIVE: 'ACTIVE',
    OFFLINE: 'OFFLINE',
    DISABLED: 'DISABLED'
};

export const UserStatusText = {
    AVAILABLE: 'AVAILABLE',
    IN_TRANSIT: 'IN TRANSIT',
    OFFLINE: 'OFFLINE',
    DISABLED: 'DISABLED'
};

export const UserEffectiveStatusEnum = {
    ACTIVE_AND_IDLE: 'ACTIVE_AND_IDLE',
    ACTIVE_AND_EXECUTING: 'ACTIVE_AND_EXECUTING',
    BUSY: 'BUSY',
    BUSY_MANUAL: 'BUSY_MANUAL',
    LOGGED_OUT: 'LOGGED_OUT',
    NEVER_LOGGED_IN: 'NEVER_LOGGED_IN',
    LOCATION_MISSING: 'LOCATION_MISSING',
    DISABLED: 'DISABLED'
};

class InternalExtendedUser extends ExtendedUser {
    static fromJS(json: ExtendedUserModelType): InternalExtendedUser {
        const state: Object = Object.assign({}, json);
        state.status = InternalUserStatusWrapper.fromJS(state.status);
        state.teams = Immutable.List(state.teams).map(item =>
            Team.fromJS(item)
        );
        state.routes = Immutable.List(state.routes).map(item =>
            ZoneMapGeometry.fromJS(item)
        );
        state.location = InternalLocationInstant.fromJS(state.location);
        state.ongoingTasks = InternalUserTaskInfo.fromJS(state.ongoingTasks);
        state.events = InternalUserEventInfo.fromJS(state.events);
        state.skills = Skills.fromJS(state.skills);
        state.effectiveStatus = InternalUserEffectiveStatus.fromJS(
            state.effectiveStatus
        );
        return new this(Immutable.Map(state));
    }

    get status(): InternalUserStatusWrapper {
        return this._state.get('status');
    }

    get location(): InternalLocationInstant {
        return this._state.get('location');
    }

    get ongoingTasks(): InternalUserTaskInfo {
        return this._state.get('ongoingTasks');
    }

    get events(): InternalUserEventInfo {
        return this._state.get('events');
    }

    get team(): Team {
        return this.teams ? this.teams.get(0) : null;
    }

    setTeams(teams: Immutable.List<Team>): this {
        return this.clone(this._state.set('teams', teams));
    }

    getStatusText(): string {
        switch (this.status.status) {
            case UserStatus.IDLE:
                return UserStatusText.AVAILABLE;
            case UserStatus.ACTIVE:
                return UserStatusText.IN_TRANSIT;
            case UserStatus.OFFLINE:
                return UserStatusText.OFFLINE;
            case UserStatus.DISABLED:
                return UserStatusText.DISABLED;
        }
        throw new Error(`Unknown user status: ${this.status.status}`);
    }

    getEffectiveStatusText(): string {
        const {
            effectiveStatus: { effectiveStatus, busyReason }
        } = this;

        if (effectiveStatus !== UserEffectiveStatusEnum.BUSY) {
            switch (effectiveStatus) {
                case UserEffectiveStatusEnum.ACTIVE_AND_IDLE:
                    return nsTranslate('userStatus.AVAILABLE');
                case UserEffectiveStatusEnum.ACTIVE_AND_EXECUTING:
                    return nsTranslate('userStatus.IN_TRANSIT');
                case UserEffectiveStatusEnum.DISABLED:
                    return nsTranslate('userStatus.DISABLED');
            }
        } else {
            switch (busyReason) {
                case UserEffectiveStatusEnum.LOCATION_MISSING:
                    return nsTranslate('userStatus.LOCATION_MISSING');
                case UserEffectiveStatusEnum.BUSY_MANUAL:
                    return nsTranslate('userStatus.BUSY_MANUAL');
                case UserEffectiveStatusEnum.LOGGED_OUT:
                case UserEffectiveStatusEnum.NEVER_LOGGED_IN:
                    return nsTranslate('userStatus.LOGGED_OUT');
            }
        }
        throw new Error(`Unknown user effectiveStatus: ${effectiveStatus}`);
    }

    get skills(): Skills {
        return this._state.get('skills');
    }

    get teams(): Immutable.List<Team> {
        return this._state.get('teams');
    }

    get effectiveStatus(): InternalUserEffectiveStatus {
        return this._state.get('effectiveStatus');
    }

    get routes(): Immutable.List<ZoneMapGeometry> {
        return this._state.get('routes');
    }
}

class InternalUserStatusWrapper extends UserStatusWrapper {
    static fromJS(json: UserStatusWrapperModelType): InternalUserStatusWrapper {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalLocationInstant extends LocationInstant {
    static fromJS(json: LocationInstantModelType): InternalLocationInstant {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalUserTaskInfo extends UserTaskInfo {
    static fromJS(json: UserTaskInfoModelType): InternalUserTaskInfo {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalUserEventInfo extends UserEventInfo {
    static fromJS(json: UserEventInfoModelType): InternalUserEventInfo {
        const state: Object = Object.assign({}, json);
        state.criticalEvents = state.criticalEvents
            ? Immutable.List(state.criticalEvents).map(item =>
                  InternalCriticalEvent.fromJS(item)
              )
            : state.criticalEvents;
        return new this(Immutable.Map(state));
    }

    get criticalEvents(): ?Immutable.List<CriticalEvent> {
        return this._state.get('criticalEvents');
    }
}

class InternalCriticalEvent extends CriticalEvent {
    static fromJS(json: CriticalEventModelType): InternalCriticalEvent {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

export {
    InternalExtendedUser as ExtendedUser,
    InternalUserStatusWrapper as UserStatusWrapper,
    InternalLocationInstant as LocationInstant,
    InternalUserTaskInfo as UserTaskInfo,
    InternalUserEventInfo as UserEventInfo,
    InternalCriticalEvent as CriticalEvent,
    InternalUserEffectiveStatus as UserEffectiveStatus
};
